import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Field } from '@vnetwork/ui/forms';
import { Input, Card, CardHeader, CardTitle, CardBody } from '@vnetwork/ui/primitives';
import { SERVICE_SUSPEND_KEY } from '@/api/tickets-constants';
import type { TicketFormValues } from '../ticket-form-schema';
import { DateInput } from '@/components/date-input';

const twoColGrid = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 'var(--vn-space-4)',
} as const;

/**
 * Priority & Schedule section.
 *
 * Date fields use datetime-local inputs and submit ISO strings via
 * dayjs().toISOString() — date-only fields (service_startdate /
 * service_duedate / service_suspend_until) submit YYYY-MM-DD.
 *
 * The service_suspend_until field only appears when ticket_type_key ===
 * SERVICE_SUSPEND_KEY, matching the legacy conditional branch.
 */
export function PriorityScheduleSection() {
  const { control, formState: { errors } } = useFormContext<TicketFormValues>();
  const typeKey = useWatch({ control, name: 'ticket_type_key' });
  const isServiceSuspend = typeKey === SERVICE_SUSPEND_KEY;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Schedule</CardTitle>
      </CardHeader>
      <CardBody>
      <div style={twoColGrid}>
        {/* Customer request date */}
        <Field
          label="Requested At"
          description="Date the customer submitted the request"
          error={String(errors.ticket_requested_at?.message ?? '') || undefined}
        >
          {(fp) => (
            <Controller
              name="ticket_requested_at"
              control={control}
              render={({ field }) => (
                <Input
                  {...fp}
                  type="datetime-local"
                  value={
                    field.value
                      ? toLocalInputValue(field.value as string)
                      : ''
                  }
                  onChange={(e) =>
                    field.onChange(e.target.value ? new Date(e.target.value).toISOString() : null)
                  }
                  invalid={!!errors.ticket_requested_at}
                />
              )}
            />
          )}
        </Field>

        {/* Estimated response (ticket_time_repply) */}
        <Field
          label="Estimated Response Time"
          error={String(errors.ticket_time_repply?.message ?? '') || undefined}
        >
          {(fp) => (
            <Controller
              name="ticket_time_repply"
              control={control}
              render={({ field }) => (
                <Input
                  {...fp}
                  type="datetime-local"
                  value={field.value ? toLocalInputValue(field.value as string) : ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? new Date(e.target.value).toISOString() : null)
                  }
                  invalid={!!errors.ticket_time_repply}
                />
              )}
            />
          )}
        </Field>

        {/* Estimated completion */}
        <Field
          label="Estimated Completion"
          error={String(errors.ticket_estimated_completion_date?.message ?? '') || undefined}
        >
          {(fp) => (
            <Controller
              name="ticket_estimated_completion_date"
              control={control}
              render={({ field }) => (
                <Input
                  {...fp}
                  type="datetime-local"
                  value={field.value ? toLocalInputValue(field.value as string) : ''}
                  onChange={(e) =>
                    field.onChange(e.target.value ? new Date(e.target.value).toISOString() : null)
                  }
                  invalid={!!errors.ticket_estimated_completion_date}
                />
              )}
            />
          )}
        </Field>

        {/* Service start date */}
        <Field label="Service Start Date">
          {(fp) => (
            <Controller
              name="service_startdate"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...fp}
                  value={(field.value as string | null) ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
              )}
            />
          )}
        </Field>

        {/* Service end date */}
        <Field label="Service End Date">
          {(fp) => (
            <Controller
              name="service_duedate"
              control={control}
              render={({ field }) => (
                <DateInput
                  {...fp}
                  value={(field.value as string | null) ?? ''}
                  onChange={(e) => field.onChange(e.target.value || null)}
                />
              )}
            />
          )}
        </Field>

        {/* Suspend until — only for service_suspend ticket type */}
        {isServiceSuspend && (
          <Field
            label="Suspend Until"
            required
            error={String(errors.service_suspend_until?.message ?? '') || undefined}
          >
            {(fp) => (
              <Controller
                name="service_suspend_until"
                control={control}
                render={({ field }) => (
                  <DateInput
                    {...fp}
                    value={(field.value as string | null) ?? ''}
                    onChange={(e) => field.onChange(e.target.value || null)}
                    invalid={!!errors.service_suspend_until}
                  />
                )}
              />
            )}
          </Field>
        )}
      </div>
      </CardBody>
    </Card>
  );
}

/**
 * Convert an ISO string to the value format expected by `datetime-local` inputs
 * (YYYY-MM-DDTHH:mm in local time, not UTC).
 *
 * Uses getFullYear/getMonth/getDate per ui-conventions.md guidance to avoid the
 * UTC-shift that `toISOString().slice(0,16)` causes in UTC+7.
 */
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
